import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { promotionService } from "../services/promotion.service";
import { PromotionListDetails } from "../models/response/PromotionListResponse";
import { Button } from "../../../components/ui";
import localization from "../../../config/localization";
import { useLanguage } from "../../../config/localization/LanguageProvider";
import { SupportedLanguages } from "../../../models/constants/supportedLanguages";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { BLOB_IMAGE_URL } from "../../../config/appConfig";

const tabs = ["All", "Sports", "Casino"];

export function Promotion() {
  const [activeTab, setActiveTab] = useState("All");
  const [data, setData] = useState<PromotionListDetails[]>([]);
  const navigate = useNavigate();

  const { language } = useLanguage();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const res = await promotionService.getPromotions();
    if (res.isSuccess && res.data?.details?.length) {
      setData(res.data.details);
    }
  };

  const getCategoryImage = (
    category: PromotionListDetails["category"]
  ): string => {
    switch (category) {
      case "Sports":
        return "/icons/ic-sports.svg";
      case "Exchange":
        return "/icons/ic-exchange.svg";
      case "Casino":
        return "/icons/ic-casino.svg";
      default:
        return "/images/default.png";
    }
  };


  const filteredData =
    activeTab === "All"
      ? data
      : data.filter(
        (x) => x.category?.toLowerCase() === activeTab.toLowerCase()
      );

  return (
    <div className="mx-auto min-h-screen px-4 py-4 text-white">
      <h2 className="mb-4 text-xl font-bold">{localization.promotion}</h2>

      {/* Tabs */}
      <div className="mb-6 flex gap-8 border-b border-gray-800 pb-4">
        {tabs.map((tab) => (
          <Button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`relative pb-2 text-sm font-medium ${activeTab === tab ? "text-white" : "text-gray-400"
              }`}
          >
            {localization.getString(tab.toLowerCase())}
            {activeTab === tab && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-cyan-400" />
            )}
          </Button>
        ))}
      </div>

      {/* Promotion Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {filteredData.map((item) => (
          <div
            key={item.pageurl}
            onClick={() => navigate(`/promotion/${item.pageurl}`)}
            className="relative min-h-[140px] cursor-pointer overflow-hidden rounded-2xl border border-gray-700/50 transition-all hover:border-cyan-500/50"
          >
            {/* Background */}
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: `url(${BLOB_IMAGE_URL}promotion/${item.promotionimage})`,
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-transparent" />
            </div>

            {/* Category */}
            <div className="relative p-4">
              <div className="inline-flex items-center gap-1 rounded border border-white/30 px-2">
                <img
                  src={getCategoryImage(item.category)}
                  className="h-6 w-6"
                  alt={item.category ?? ""}
                />
                <span className="text-sm font-bold">{localization.getString(item.category?.toLowerCase() ?? "")}</span>
              </div>
            </div>

            {/* Content */}
            <div className="relative flex min-h-[140px] flex-col justify-between p-5">
              <p className="line-clamp-2 max-w-[60%] text-xl font-bold">
                {language === SupportedLanguages.Hindi ? item.titlehindi : item.title}
              </p>

              <div className="flex items-center justify-between gap-4">
                <p className="line-clamp-1 text-xs text-gray-300">
                  {language === SupportedLanguages.Hindi ? item.descriptionhindi : item.description}
                </p>
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-all">
                  <FontAwesomeIcon icon={faArrowRight} size="xs" />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
